import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers, extractApiData } from '@/api/core';
import type { Company, UpdateCompanyRequest, CompanyManagerResponse } from '@packages/types/company';
import {
  applyCompanyUpdateToQueryCache,
  invalidateCompanyRelatedQueries,
} from '@/api/cache/organizationMutationCache';

// Query Keys
const companyKeys = {
  all: ['companies'] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: number) => [...companyKeys.details(), id] as const,
  companyManager: (companyId: number) =>
    [...companyKeys.all, 'company-manager', companyId] as const,
};

// 특정 회사 정보 조회
export const useGetCompanyById = (id: number) => {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: async () => {
      const response = await apiHelpers.get<Company>('company.getById', { id });
      return extractApiData(response);
    },
    enabled: !!id,
  });
};

// 특정 회사의 CompanyManager 정보 조회 (Admin용)
export const useGetCompanyManagerById = (companyId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: companyKeys.companyManager(companyId),
    queryFn: async () => {
      const response = await apiHelpers.get<CompanyManagerResponse | null>('company.getCompanyManagerById', { id: companyId });
      return extractApiData(response);
    },
    enabled: !!companyId && options?.enabled !== false,
    ...options,
  });
};

// 회사 정보 수정
export const useUpdateCompany = (companyId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updateData: UpdateCompanyRequest) => {
      const response = await apiHelpers.put<Company>('company.getById', updateData, {
        id: companyId,
      });
      return extractApiData(response);
    },
    onSuccess: (updatedCompany: Company) => {
      applyCompanyUpdateToQueryCache(queryClient, updatedCompany);
      invalidateCompanyRelatedQueries(queryClient);
    },
  });
};
